'use client';

import React, { useEffect, ComponentProps } from 'react';
import { motion } from 'framer-motion';
import classNames from 'classnames';
import Image from 'next/image';
import AnimatedGradientButton from '@/app/components/shared/AnimatedGradientButton';
import { useIsMobile } from '@/hooks/useIsMobile';
import { getAnimationConfiguration } from '@/app/components/shared/animationHelpers';

// Import image assets (desktop only)
import ellaDesktop from '@/assets/images/ella-new.jpeg';
import serinaDesktop from '@/assets/images/serina.png';
import ellaMobile from '@/assets/images/ella-new-mobile.jpeg';

const DURATION = 0.3;

type CardTheme = 'serina' | 'ella';

function getImageSource(theme: CardTheme) {
  return theme === 'serina' ? serinaDesktop : ellaDesktop;
}

function getThemeConfiguration({ theme }: { theme: CardTheme }): {
  effectiveTheme: CardTheme;
  buttonTheme: 'purple' | 'orange';
} {
  let buttonTheme: 'purple' | 'orange';
  if (theme === 'serina') {
    buttonTheme = 'purple';
  } else {
    buttonTheme = 'orange';
  }

  return {
    buttonTheme,
    effectiveTheme: theme,
  };
}

function getUIConfiguration<T extends string>({
  cardKey,
  isExpanded,
  isOtherCardSelected,
}: {
  isExpanded: boolean;
  cardKey: T;
  isOtherCardSelected: boolean;
}) {
  let buttonText: string;
  if (isExpanded) {
    buttonText = 'Close';
  } else {
    buttonText = 'Bio';
  }

  let toggleValue: T | undefined;
  if (isExpanded) {
    toggleValue = undefined;
  } else {
    toggleValue = cardKey;
  }

  let opacity: number;
  if (isOtherCardSelected) {
    opacity = 0.2;
  } else {
    opacity = 1;
  }

  return {
    buttonText,
    opacity,
    toggleValue,
  };
}

interface BioCardProps<T extends string> extends Omit<
  ComponentProps<'div'>,
  'children' | 'onToggle'
> {
  name: string;
  title: string;
  bio: React.ReactNode;
  cardKey: T;
  currentCard: T | undefined;
  onToggle: (card: T | undefined) => void;
  theme?: CardTheme;
  className?: string;
  solidBackground?: boolean;
}

export default function BioCard<T extends string>({
  bio,
  cardKey,
  className = '',
  currentCard,
  name,
  onToggle,
  solidBackground = false,
  theme = 'serina',
  title,
}: BioCardProps<T>) {
  const isExpanded = currentCard === cardKey;
  const isOtherCardSelected =
    currentCard !== undefined && currentCard !== cardKey;
  const isSerina =
    cardKey === 'Serina' ||
    cardKey === 'serina' ||
    cardKey === 'Brian' ||
    cardKey === 'brian';

  const isMobile = useIsMobile();
  // Get all configuration values using destructuring
  const { animateX, animateY, dynamicZIndex, initialX, initialY } =
    getAnimationConfiguration({
      isExpanded,
      isMobile,
      isSerina,
    });

  const { buttonTheme, effectiveTheme } = getThemeConfiguration({
    theme,
  });

  const imageSource = getImageSource(effectiveTheme);

  const { buttonText, opacity, toggleValue } = getUIConfiguration({
    cardKey,
    isExpanded,
    isOtherCardSelected,
  });

  // Scroll to expanded content on mobile when card expands
  useEffect(() => {
    if (isMobile && isExpanded) {
      const timeoutId = setTimeout(() => {
        const expandedElement = document.getElementById(`${cardKey}CardInfo`);
        if (expandedElement) {
          expandedElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 100); // Small delay to let the animation begin

      return () => clearTimeout(timeoutId);
    }
  }, [isExpanded, isMobile, cardKey]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity }}
      transition={{ duration: DURATION }}
      className={`relative min-h-[420px] w-full ${isMobile && isExpanded && 'mb-[350px]'}`}
      data-component-name="BioCard-Container"
    >
      {/* Founder Image Card - Always Visible */}
      <motion.div
        layout
        className={`flex h-full w-full flex-col items-center justify-end gap-2 rounded-xl px-8 py-6 pb-[50px] shadow-[0px_1px_20.3px_2px_#0000002b] md:relative md:z-20 md:items-start md:justify-between md:gap-0 md:py-6 md:pb-6 ${solidBackground ? (effectiveTheme === 'serina' ? 'bg-[#e8ddf6]' : 'bg-[#fdd5c6]') : 'overflow-hidden'} ${className}`}
        data-component-name="BioCard-Collapsed"
      >
        {!solidBackground && (
          <Image
            src={!isSerina && isMobile ? ellaMobile : imageSource}
            alt={`${name} portrait`}
            fill
            sizes="(max-width: 768px) min(100vw, 600px), (max-width: 1200px) min(50vw, 600px), (max-width: 1600px) min(40vw, 750px), (max-width: 2000px) min(600px, 750px), 750px"
            className={`object-cover ${isSerina ? 'object-[70%_center]' : 'object-[80%_30%]'} rounded-xl shadow-[0px_1px_20.3px_2px_#0000002b]`}
            priority
            placeholder="blur"
            blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k="
          />
        )}
        {/* Text content + button */}
        <motion.div
          layout
          className="absolute right-8 bottom-6 left-8 z-10 flex flex-col items-center gap-3 md:static md:flex md:h-full md:flex-col md:items-start md:justify-between md:gap-0"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: DURATION,
          }}
        >
          <div className="space-y-2 md:space-y-0.5">
            <motion.h3
              layout
              className={`pr-4 font-[Spectral] text-[30px] leading-12 font-normal text-white [text-shadow:none] md:text-[28px] md:leading-6 md:font-semibold ${isSerina && 'md:text-black'} md:[text-shadow:_0_-0.5px_0px_#fff,_0.5px_0_0px_#fff,_0_0.5px_0px_#fff,_-0.5px_0_0px_#fff]`}
            >
              {name}
            </motion.h3>
            <motion.span
              layout
              className={`pr-4 font-[Work_Sans] text-[20px] font-normal text-white [text-shadow:none] md:text-[18px] ${isSerina && 'md:text-black'} md:[text-shadow:_0_-0.5px_0px_#fff,_0.5px_0_0px_#fff,_0_0.5px_0px_#fff,_-0.5px_0_0px_#fff]`}
            >
              {title}
            </motion.span>
            <motion.div layout className="mt-2 md:mt-0 md:self-end">
              <AnimatedGradientButton
                flatHover={true}
                text={buttonText}
                as="button"
                onClick={() => onToggle(toggleValue)}
                theme={buttonTheme}
                className="w-full md:w-[100px]"
              />
            </motion.div>
          </div>
        </motion.div>
      </motion.div>

      {/* Bio Content Card - Slides out from behind */}
      {(!isMobile || isExpanded) && (
        <motion.div
          initial={{
            x: initialX,
            y: initialY,
          }}
          animate={{
            x: animateX,
            y: animateY,
          }}
          transition={{
            duration: DURATION * 1.5,
            ease: [0.25, 0.46, 0.45, 0.94], // Custom easing for smooth slide
          }}
          className={classNames(
            'absolute my-auto box-border flex h-full w-full min-w-full flex-col items-center justify-between overflow-auto rounded-none font-[Work_Sans] text-base leading-7 font-normal tracking-normal transition-[padding,left,right] md:min-h-[280px] md:min-w-[400px] md:items-start md:justify-center md:rounded-xl md:text-sm md:leading-[23px]',
            // Dynamic z-index: Serina's expanded card on mobile should be on top
            dynamicZIndex,
            {
              // Background colors with responsive padding
              'bg-[#e8ddf6]': isSerina,
              'bg-[#fdd5c6]': !isSerina,
              // Mobile padding (same for both Serina and Ella)
              'p-4 text-[14px]': isMobile, // Mobile: thicker top padding
              // Ella padding (orange background)
              'pr-[calc(2rem+10%)] pl-8': !isSerina && !isMobile, // Desktop: thicker right padding

              // Serina padding (purple background)
              'pr-8 pl-[calc(2rem+10%)]': isSerina && !isMobile, // Desktop: thicker left padding

              // Desktop horizontal positioning (unchanged)
              'top-0 bottom-0 left-[90%]': !isMobile && isSerina && isExpanded,
              'top-0 bottom-0 left-full': !isMobile && isSerina && !isExpanded,
              'top-0 right-[90%] bottom-0': !isMobile && !isSerina,
              'top-0 right-full bottom-0':
                !isMobile && !isSerina && !isExpanded,
              // Mobile vertical positioning - sits below the image
              'top-full right-0 left-0': isMobile,
            }
          )}
          id={`${cardKey}CardInfo`}
          data-component-name="BioCard-Expanded"
        >
          <p>{bio}</p>
        </motion.div>
      )}
    </motion.div>
  );
}
